import {
  MEMORY_VALUE_LIMITS,
  type MemoryKindValue,
  memoryTextKindValues,
  memoryVocabularyCodesV1,
  type StaffMemoryFact,
} from "@lafayette/schemas";

// The value a staff member types or picks for a fact. A kind takes a CODE of the closed vocabulary; only
// `DISLIKED_EXPERIENCE` and `EXPLICIT_INSTRUCTION` also take a short text. The server classifies the value
// again and refuses what is not allowed (422, field name only). This file only stops an obvious mistake
// before a request leaves the browser. It never decides what is allowed.

export type ValueDraft = Readonly<{
  /** `code`: a choice from the list. `text`: the staff member's own short text (two kinds only). */
  mode: "code" | "text";
  code: string;
  text: string;
}>;

export const emptyValueDraft: ValueDraft = { mode: "code", code: "", text: "" };

export const allowsText = (kind: MemoryKindValue): boolean =>
  (memoryTextKindValues as readonly string[]).includes(kind);

/** The string the API takes as `value`. */
export function valueFromDraft(kind: MemoryKindValue, draft: ValueDraft): string {
  return draft.mode === "text" && allowsText(kind) ? draft.text.trim() : draft.code;
}

/** An error sentence, or `undefined` when the draft may be sent. */
export function validateValueDraft(kind: MemoryKindValue, draft: ValueDraft): string | undefined {
  if (draft.mode === "text" && allowsText(kind)) {
    const length = draft.text.trim().length;
    if (length === 0) return "Write the text, or choose a value from the list.";
    if (length < MEMORY_VALUE_LIMITS.textMin) {
      return `Use at least ${MEMORY_VALUE_LIMITS.textMin} characters.`;
    }
    if (length > MEMORY_VALUE_LIMITS.textMax) {
      return `Use at most ${MEMORY_VALUE_LIMITS.textMax} characters.`;
    }
    return undefined;
  }
  if (draft.code === "") return "Choose a value.";
  return memoryVocabularyCodesV1[kind].includes(draft.code)
    ? undefined
    : "Choose a value from the list.";
}

/** The draft that holds a fact's own value, to start a correction from. A tombstone has no value. */
export function draftFromFact(fact: Pick<StaffMemoryFact, "kind" | "form" | "value">): ValueDraft {
  if (fact.value === null) return emptyValueDraft;
  if (fact.form === "TEXT" && allowsText(fact.kind)) {
    return { mode: "text", code: "", text: fact.value };
  }
  return { mode: "code", code: fact.value, text: "" };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuid = (value: string): boolean => UUID.test(value.trim());
